import { UserMe } from "@/app/dashboard/types";
import {
  citizenDigitalIdentityIdentifiersMap,
  citizenInfoIdentifiers,
  citizenInfoUserKeys,
} from "@/configs/configs";
import type { RouterOutputs } from "@ept/api";
import {
  DIGITAL_IDENTITY_CATEGORY_SLUG,
  DIGITAL_IDENTITY_GROUP_NAME,
  isNotUndefined,
} from "@ept/shared";
import _ from "lodash";

export const getFormDefaultValues = (submittedForm: {
  submittedForm: RouterOutputs["submittedFormsRouter"]["getSubmittedFormByIdentifier"]["submittedForm"];
  attachmentMap?: RouterOutputs["submittedFormsRouter"]["getSubmittedFormByIdentifier"]["attachmentMap"];
}) => {
  return (
    submittedForm?.submittedForm?.submittedFormFields.reduce(
      (acc, field) => {
        if (field.attachmentIds) {
          // it was a file input (either single or multiple files)
          acc[field.elementId!] = field.attachmentIds
            .map((attachmentId) => {
              const attachment = submittedForm.attachmentMap?.[attachmentId];
              if (!attachment) {
                return undefined;
              }
              return {
                id: attachmentId,
                name: attachment.fileName,
                url: attachment.url,
              };
            })
            .filter(isNotUndefined);
        } else if (
          typeof field.value === "string" ||
          typeof field.value === "boolean" ||
          typeof field.value === "number"
        ) {
          // it was a simple input
          acc[field.elementId!] = field.value as string | boolean;
        } else {
          // it means the input was a table
          const tableInputEntries = Object.values(
            _.groupBy(
              field.value as {
                columnId: string;
                rowNumber: number;
                value: string | boolean;
              }[],
              "rowNumber",
            ),
          ).map((row) => {
            return row.reduce(
              (prevValues, rowValues) => ({
                ...prevValues,
                [rowValues.columnId]: rowValues.value,
              }),
              {},
            );
          });
          acc[field.elementId!] = tableInputEntries;
        }
        return acc;
      },
      {} as Record<
        string,
        string | boolean | { id: string; name: string | undefined; url: string }[] | object
      >,
    ) ?? {}
  );
};

export const getUnsubmittedFormDefaultValues = (
  user: Exclude<UserMe, null>,
  formData: RouterOutputs["formManagementRouter"]["getFormDataBySlug"],
) => {
  if (!formData) {
    return {};
  }

  const formElements = formData.sections.flatMap((section) => section.section.elements).flat();

  if (
    formData.formCategory.slug === DIGITAL_IDENTITY_CATEGORY_SLUG &&
    formData.group?.displayName === DIGITAL_IDENTITY_GROUP_NAME &&
    user.profile.citizenProfile?.status === "REJECTED"
  ) {
    const defaultValues = formElements
      .filter((element) => {
        return Object.keys(citizenDigitalIdentityIdentifiersMap).includes(
          element.element.identifier,
        );
      })
      .map((element) => {
        return [
          element.id!,
          _.get(
            user.profile.citizenProfile,
            citizenDigitalIdentityIdentifiersMap[
              element.element.identifier as keyof typeof citizenDigitalIdentityIdentifiersMap
            ],
          ),
        ];
      });

    return Object.fromEntries(defaultValues);
  }

  const defaultCitizenValues: string[][] = formElements
    .filter((element) => {
      return Object.values(citizenInfoIdentifiers).includes(element.element.identifier);
    })
    .map((element) => {
      return [
        element.id!,
        _.get(
          user.profile.citizenProfile,
          citizenInfoUserKeys[element.element.identifier as keyof typeof citizenInfoIdentifiers],
        ),
      ];
    });

  return Object.fromEntries(defaultCitizenValues);
};
